import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, FileArchive, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api as calApi } from "@/lib/api";

/**
 * v84.46 — Import Customer History dari file iCal (.ics / .zip / .rar) langsung
 * ke DB produksi via admin endpoint. Idempotent (dedupe by wo_number).
 */
export default function ImportCustomerHistory() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  function pickFiles(e) {
    const chosen = Array.from(e.target.files || []);
    setFiles(chosen);
    setResult(null);
  }

  function fmtBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024*1024) return `${(n/1024).toFixed(1)} KB`;
    return `${(n/1024/1024).toFixed(1)} MB`;
  }

  async function upload() {
    if (!files.length) { toast.error("Pilih 1+ file dulu"); return; }
    // Warn if > 40 MB combined
    const total = files.reduce((s, f) => s + f.size, 0);
    if (total > 40 * 1024 * 1024) {
      if (!window.confirm(`Total ${fmtBytes(total)} — cukup besar. Lanjutkan?`)) return;
    }
    setLoading(true); setProgress(0); setResult(null);
    try {
      const res = await calApi.customerHistoryImport(files, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      setResult(res);
      const t = res.totals || {};
      toast.success(`Import selesai — ${t.new_docs || 0} customer baru, ${t.merged_existing || 0} di-merge, ${t.wo_added || 0} WO ditambah`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Gagal upload";
      toast.error(`Upload gagal: ${msg}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="border border-border bg-card rounded-sm p-4 space-y-3" data-testid="import-customer-history-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-500/10 text-blue-500 grid place-items-center flex-shrink-0">
          <FileArchive className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Import Customer History (iCal)</div>
          <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
            Upload file <code className="text-[10px] bg-muted px-1 rounded">.ics</code> / <code className="text-[10px] bg-muted px-1 rounded">.zip</code> / <code className="text-[10px] bg-muted px-1 rounded">.rar</code> berisi
            event kalender historis (2019-2025). Data akan di-parse untuk mengisi
            koleksi <code className="text-[10px] bg-muted px-1 rounded">customer_history</code> (search
            customer lama + autofill alamat). Idempotent — WO yang sudah ada akan
            di-skip (dedupe by UID).
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".ics,.ical,.zip,.rar"
          onChange={pickFiles}
          className="hidden"
          data-testid="ics-file-input"
        />
        <Button
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          className="rounded-sm gap-1.5"
          data-testid="ics-pick-btn"
        >
          <Upload className="w-4 h-4" /> Pilih file
        </Button>
        {files.length > 0 && (
          <div className="text-[11px] text-muted-foreground">
            {files.length} file · {fmtBytes(files.reduce((s, f) => s + f.size, 0))}
          </div>
        )}
        <Button
          onClick={upload}
          disabled={loading || !files.length}
          className="rounded-sm gap-1.5 bg-blue-600 hover:bg-blue-700 text-white ml-auto"
          data-testid="ics-upload-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {loading ? `Mengupload ${progress}%` : "Import Sekarang"}
        </Button>
      </div>

      {files.length > 0 && !result && (
        <div className="text-[11px] text-muted-foreground max-h-24 overflow-auto space-y-0.5">
          {files.map((f, i) => (
            <div key={i} className="flex justify-between gap-2">
              <span className="truncate">{f.name}</span>
              <span className="text-muted-foreground/70 font-mono flex-shrink-0">{fmtBytes(f.size)}</span>
            </div>
          ))}
        </div>
      )}

      {result && (
        <div className="border-t border-border pt-3 space-y-2" data-testid="ics-result">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            <Stat label="Total Event" value={result.totals?.events || 0} />
            <Stat label="Unique Phone" value={result.totals?.unique_phones || 0} />
            <Stat label="Customer Baru" value={result.totals?.new_docs || 0} highlight />
            <Stat label="WO Ditambah" value={result.totals?.wo_added || 0} highlight />
          </div>
          <div className="text-[10px] text-muted-foreground space-y-0.5 max-h-32 overflow-auto">
            {result.per_file?.map((p, i) => (
              <div key={i} className="flex items-center gap-1.5">
                {p.ok
                  ? <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                  : <XCircle className="w-3 h-3 text-red-500 flex-shrink-0" />}
                <span className="truncate flex-1">{p.filename}</span>
                <span className="font-mono flex-shrink-0">
                  {p.ok ? `${p.events || 0} ev · ${p.unique_phones || 0} ph` : p.error}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-sm px-2 py-1.5 ${highlight ? "bg-blue-500/10 border border-blue-500/20" : "bg-muted"}`}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-mono font-bold text-sm ${highlight ? "text-blue-500" : "text-foreground"}`}>{value.toLocaleString("id-ID")}</div>
    </div>
  );
}
